import * as React from 'react'
import { Card, CardContent, Switch, Label, Separator } from '@/shared/ui'
import { getMaintenanceMode, setMaintenanceMode } from '../services/settingsService'
import { useToast } from '@/shared/ui/Toast'
import { getLogo, setLogoConfig } from '@/modules/branding/brandingService'
import { uploadToBucket } from '@/modules/upload/services/uploadService'
import LogoUploadField from '@/modules/branding/components/LogoUploadField'

export default function MaintenanceSettings() {
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [on, setOn] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [logoUrl, setLogoUrl] = React.useState<string | null>(null)
  const [uploading, setUploading] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const cur = await getMaintenanceMode()
        if (!cancelled) setOn(!!cur)
        const curLogo = await getLogo('maintenance', true)
        if (!cancelled) setLogoUrl(curLogo)
      } catch (e:any) {
        if (!cancelled) setError(e?.message || 'Falha ao carregar estado de manutenção')
      } finally { if (!cancelled) setLoading(false) }
    })()
    return () => { cancelled = true }
  }, [])

  async function handleToggle(next: boolean) {
    setError(null)
    try {
      setSaving(true)
      await setMaintenanceMode(next)
      setOn(next)
      toast({ title: 'Manutenção', description: next ? 'Modo manutenção ATIVADO.' : 'Modo manutenção DESATIVADO.', variant: 'success', soft: true })
    } catch (e:any) {
      setError(e?.message || 'Falha ao salvar modo manutenção')
    } finally { setSaving(false) }
  }

  async function handleUpload(file: File) {
    setError(null)
    setUploading(true)
    try {
      const { url } = await uploadToBucket('branding', file, 'maintenance/light')
      await setLogoConfig('maintenance', url, null)
      setLogoUrl(url)
      toast({ title: 'Manutenção', description: 'Logo atualizado com sucesso.', variant: 'success', soft: true })
    } catch (e:any) {
      setError(e?.message || 'Falha ao enviar logo de manutenção')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Card>
      <CardContent className="space-y-4 pt-4">
        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>
        )}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h3 className="text-base font-medium">Modo manutenção</h3>
            <p className="text-sm text-foreground/70">Quando ativado, o site exibe uma tela preta com o logo e o texto “em breve...”. A rota <code>/login</code> permanece acessível e o usuário <strong>master</strong> logado visualiza todo o sistema (manutenção desativada para ele).</p>
          </div>
          <div className="flex items-center gap-3">
            <Label>{on ? 'Ativado' : 'Desativado'}</Label>
            <Switch checked={on} onChange={(e)=> handleToggle((e.target as HTMLInputElement).checked)} disabled={loading || saving} />
          </div>
        </div>

        <Separator className="my-4" />
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Logo da página de manutenção</h4>
          <p className="text-xs text-foreground/70">Imagem exibida na tela preta de manutenção. Recomendada versão “Light” (fundo escuro).</p>
          <LogoUploadField label="Logo (Light)" url={logoUrl} disabled={saving || uploading} onSelect={handleUpload} />
        </div>

        <div className="text-xs text-foreground/60 mt-4">
          SSOT: armazenado em <code>app_settings</code> no Supabase. O app lê via <code>/api/maintenance</code>.
        </div>
      </CardContent>
    </Card>
  )
}

